import { useCallback, useMemo, useState } from "react";
import {
  LoadingIndicator,
  Typography,
  Banner,
  BannerTypes,
} from "@sunrun/experience-ui-components";
import styled from "@emotion/styled/macro";
import {
  HeaderButton,
  HeaderContentLayout,
} from "components/templates/HeaderContentLayout";
import { DocumentUpload } from "components/organisms/DocumentUpload";
import { Slideout } from "components/molecules/ConfirmInfo/Slideout";
import { ErrorComponent } from "components/atoms/ErrorComponent";
import { ErrorModal } from "components/molecules/ErrorModal";
import { UsageFormContextProvider } from "providers/UsageFormContext";
import { useCheckoutAttachmentsNoType } from "checkout/hooks/useCheckoutSwr";
import { useCheckoutContext } from "checkout/providers/CheckoutContext";
import { useAuthToken } from "hooks/useAuthToken";
import { useUsageData } from "utils/swrHooks";
import { Attachment } from "utils/usageUtils";
import { deleteAttachment, postCheckoutAttachment } from "services/prospect";
import { hasSubmittedToContractReview } from "constants/prospect";

type CheckoutUtilityBillSlideoutProps = {
  onClose: () => void;
  onGetBillOnline?: () => void;
  onUploadSuccess?: () => void;
  intervalDataInitialLoading?: boolean;
  intervalDataStatus?: string | null;
};

const CheckoutUtilityBillSlideoutContent = ({
  onClose,
  onGetBillOnline,
  onUploadSuccess,
  intervalDataInitialLoading,
  intervalDataStatus,
}: CheckoutUtilityBillSlideoutProps) => {
  const { prospectId, proposalId, refreshTasks } = useCheckoutContext();
  const authKey = useAuthToken("HYBRID")?.token || "";
  const [isDocumentLoading, setIsDocumentLoading] = useState(false);
  const [isAttachmentsRefreshing, setIsAttachmentsRefreshing] = useState(false);
  const [attachmentError, setAttachmentError] = useState<Error | null>(null);

  const { prospect: usage, error: usageError } = useUsageData(prospectId);

  const {
    data: checkoutAttachments,
    error: attachmentsError,
    isLoading: attachmentsLoading,
    mutate: mutateCheckoutAttachments,
  } = useCheckoutAttachmentsNoType(prospectId, proposalId);

  const utilityBillAttachments =
    checkoutAttachments?.filter((doc) => doc.documentType === "Utility Bill") ??
    [];

  const isUtilityBillDeleteBlocked = hasSubmittedToContractReview(
    usage?.leadStage
  );

  const handleAttachmentUploadSuccess = useCallback(async () => {
    setIsAttachmentsRefreshing(true);
    try {
      await mutateCheckoutAttachments();
      await refreshTasks();
      onUploadSuccess?.();
    } finally {
      setIsAttachmentsRefreshing(false);
    }
  }, [mutateCheckoutAttachments, refreshTasks, onUploadSuccess]);

  const immediateUpload = useMemo(
    () => ({
      upload: (attachmentsToUpload: Attachment[]) =>
        Promise.all(
          attachmentsToUpload.map((attachment) =>
            postCheckoutAttachment(authKey, prospectId, proposalId, attachment)
          )
        ).then(() => undefined),
      onSuccess: handleAttachmentUploadSuccess,
      onError: (error: Error) => setAttachmentError(error),
      onLoadingChange: setIsDocumentLoading,
    }),
    [authKey, prospectId, proposalId, handleAttachmentUploadSuccess]
  );

  const handleDeleteUtilityBill = useCallback(
    async (file: Attachment & { documentId: string }) => {
      try {
        await deleteAttachment(authKey, prospectId, file.documentId);
        setIsAttachmentsRefreshing(true);
        await mutateCheckoutAttachments();
        await refreshTasks();
      } catch (err) {
        setAttachmentError(err as Error);
        throw err;
      } finally {
        setIsAttachmentsRefreshing(false);
      }
    },
    [authKey, prospectId, mutateCheckoutAttachments, refreshTasks]
  );

  const buttons = useMemo(
    () =>
      [
        {
          type: "secondary",
          text: "Close",
          onClick: onClose,
          disabled: isDocumentLoading,
        },
      ] as HeaderButton[],
    [onClose, isDocumentLoading]
  );

  if (usageError || attachmentsError) {
    return (
      <Slideout>
        <HeaderContentLayout
          content={
            <ErrorComponent
              context="CheckoutUtilityBillSlideout"
              action="Get Utility Bill Attachments"
              error={(usageError || attachmentsError) as Error}
            />
          }
          title="There was a Problem."
          description="Something went wrong. Please try refreshing the page."
        />
      </Slideout>
    );
  }

  if (attachmentsLoading || isAttachmentsRefreshing) {
    return (
      <Slideout>
        <LoadingIndicator fullScreen color="black" />
      </Slideout>
    );
  }

  return (
    <Slideout onClose={onClose}>
      <div data-testid="checkout-utility-bill-slideout">
        {!!attachmentError && (
          <ErrorModal
            context="CheckoutUtilityBillSlideout"
            error={attachmentError}
            action="Uploading Utility Bill"
            onClose={() => setAttachmentError(null)}
          />
        )}
        <HeaderContentLayout
          title="Usage & Utility"
          buttons={buttons}
          content={
            <SlideoutContent>
              <Typography variant="p">
                To find your best system, we need to understand your current
                electricity rate.
              </Typography>
              <Banner
                bannerType={BannerTypes.InfoLight}
                show={true}
                showClose={false}
                primaryAction={{
                  title: "Get Bill Online",
                  action: () => {
                    onGetBillOnline?.();
                    onClose();
                  },
                  testId: "get-bill-online-button",
                }}
              >
                <Typography variant="p">
                  Use our simple online tool to get your utility bill
                  automatically.
                </Typography>
              </Banner>
              <DocumentUpload
                uploadedFiles={utilityBillAttachments}
                immediateUpload={immediateUpload}
                documentType="Utility Bill"
                description="Upload monthly utility bill(s) for review."
                title="Utility Bills"
                documentLoading={isDocumentLoading}
                getBillOnline={
                  onGetBillOnline
                    ? () => {
                        onGetBillOnline();
                        onClose();
                      }
                    : undefined
                }
                intervalDataInitialLoading={intervalDataInitialLoading}
                intervalDataStatus={intervalDataStatus}
                onDeleteUploadedFile={handleDeleteUtilityBill}
                isDeleteBlocked={isUtilityBillDeleteBlocked}
                markUploadDirty={false}
              />
            </SlideoutContent>
          }
        />
      </div>
    </Slideout>
  );
};

const CheckoutUtilityBillSlideout = (
  props: CheckoutUtilityBillSlideoutProps
) => (
  <UsageFormContextProvider>
    <CheckoutUtilityBillSlideoutContent {...props} />
  </UsageFormContextProvider>
);

const SlideoutContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

export { CheckoutUtilityBillSlideout };
export type { CheckoutUtilityBillSlideoutProps };
